// src/components/ui/cards/PartnerCard/PartnerCardShadcn.jsx
import React from 'react';
import {
  Building2,
  Mail,
  Phone,
  MapPin,
  Calendar,
  MessageCircle,
  UserX,
  ExternalLink,
  MoreVertical
} from 'lucide-react';

// shadcn components
import { Card, CardContent } from '../../card';
import { Button } from '../../button';
import { Badge } from '../../badge';
import { Avatar, AvatarFallback } from '../../avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../dropdown-menu';

import { cn } from '../../../../utils/cn';

const PartnerCardShadcn = ({
  partner,
  viewMode = 'grid',
  onMessage,
  onViewDetails,
  onDisconnect,
  loading = false,
  className = ''
}) => {
  const handleMessage = (e) => {
    e.stopPropagation();
    if (onMessage && !loading) {
      onMessage();
    }
  };

  const handleDisconnect = (e) => {
    e.stopPropagation();
    if (onDisconnect && !loading) {
      onDisconnect();
    }
  };

  const handleViewDetails = () => {
    if (onViewDetails && !loading) {
      onViewDetails();
    }
  };

  // Extract connected date
  const connectedDate = partner.connected_at || partner.created_at;
  const formattedDate = connectedDate
    ? new Date(connectedDate).toLocaleDateString()
    : 'Recently';

  if (viewMode === 'list') {
    return (
      <Card
        className={cn(
          "cursor-pointer hover:bg-accent/50 transition-colors",
          loading && "opacity-50 pointer-events-none",
          className
        )}
        onClick={handleViewDetails}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-blue-600 text-white">
                  <Building2 className="w-5 h-5" />
                </AvatarFallback>
              </Avatar>

              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-foreground truncate">
                  {partner.name}
                </h3>
                <p className="text-sm text-muted-foreground truncate">
                  {partner.network_profile?.profile_description || 'Business Partner'}
                </p>
              </div>

              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                {partner.contact_email && (
                  <div className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    <span>{partner.contact_email}</span>
                  </div>
                )}
                {partner.address && (
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{partner.address}</span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  <span>Connected {formattedDate}</span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 ml-4">
              <Button variant="outline" size="sm" onClick={handleMessage}>
                <MessageCircle className="w-4 h-4 mr-2" />
                Message
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <MoreVertical className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleViewDetails}>
                    <ExternalLink className="w-4 h-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleDisconnect} className="text-red-600">
                    <UserX className="w-4 h-4 mr-2" />
                    Disconnect
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Grid view
  return (
    <Card
      className={cn(
        "cursor-pointer hover:bg-accent/50 transition-all duration-300 group",
        loading && "opacity-50 pointer-events-none",
        className
      )}
      onClick={handleViewDetails}
    >
      <CardContent className="p-6">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-4">
            <Avatar className="h-12 w-12">
              <AvatarFallback className="bg-gradient-to-br from-emerald-500 to-blue-600 text-white">
                <Building2 className="w-6 h-6" />
              </AvatarFallback>
            </Avatar>

            <div>
              <h3 className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                {partner.name}
              </h3>
              <p className="text-muted-foreground text-sm">
                {partner.network_profile?.profile_description || 'Business Partner'}
              </p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleViewDetails}>
                <ExternalLink className="w-4 h-4 mr-2" />
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleDisconnect} className="text-red-600">
                <UserX className="w-4 h-4 mr-2" />
                Disconnect
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Partner Details */}
        <div className="space-y-3 mb-4">
          {partner.contact_email && (
            <div className="flex items-center gap-3 text-sm">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <span className="text-foreground">{partner.contact_email}</span>
            </div>
          )}

          {partner.contact_phone && (
            <div className="flex items-center gap-3 text-sm">
              <Phone className="w-4 h-4 text-muted-foreground" />
              <span className="text-foreground">{partner.contact_phone}</span>
            </div>
          )}

          {partner.address && (
            <div className="flex items-center gap-3 text-sm">
              <MapPin className="w-4 h-4 text-muted-foreground" />
              <span className="text-foreground">{partner.address}</span>
            </div>
          )}

          <div className="flex items-center gap-3 text-sm">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <span className="text-muted-foreground">Connected {formattedDate}</span>
          </div>
        </div>

        {/* Business Type Badge */}
        {partner.business_type && (
          <div className="mb-4">
            <Badge variant="secondary">
              {partner.business_type}
            </Badge>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleMessage} className="flex-1">
            <MessageCircle className="w-4 h-4 mr-2" />
            Message
          </Button>
          <Button variant="outline" size="sm" onClick={handleViewDetails}>
            <ExternalLink className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default PartnerCardShadcn;